import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployeeContext } from '@/hooks/useEmployeeContext';
import {
  getNotificationPreferences,
  updateMe,
  updateNotificationPreferences,
} from '@/services/api.client';
import type { NotificationPreferences } from '@/types';

// ─── Profile tab ──────────────────────────────────────────────────────────────

const profileSchema = z.object({
  phone: z.string().optional(),
});
type ProfileValues = z.infer<typeof profileSchema>;

function ProfileTab() {
  const { user, refetch } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { phone: user?.phone ?? '' },
  });

  useEffect(() => {
    if (user) reset({ phone: user.phone ?? '' });
  }, [user, reset]);

  const mutation = useMutation({
    mutationFn: (v: ProfileValues) =>
      updateMe({ ...(v.phone !== undefined ? { phone: v.phone } : {}) }),
    onSuccess: () => {
      void refetch();
      toast.success('Profile updated');
    },
    onError: () => toast.error('Failed to update profile'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
        <CardDescription>Your account details.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 space-y-3">
          <div>
            <p className="text-xs text-muted-foreground">Full name</p>
            <p className="text-sm font-medium">{user?.fullName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="text-sm font-medium">{user?.email}</p>
          </div>
        </div>
        <form onSubmit={(e) => void handleSubmit((v) => mutation.mutate(v))(e)} noValidate>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="phone">Phone</FieldLabel>
              <Input
                id="phone"
                type="tel"
                placeholder="+254 700 000 000"
                aria-label="Phone"
                {...register('phone')}
              />
            </Field>
            <Field>
              <Button type="submit" disabled={mutation.isPending || !isDirty}>
                {mutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  );
}

// ─── Notification toggle row ──────────────────────────────────────────────────

interface ToggleRowProps {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ id, title, description, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="flex-1">
        <Label htmlFor={id} className="text-sm font-medium leading-none">
          {title}
        </Label>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

// ─── Notifications tab ────────────────────────────────────────────────────────

function NotificationsTab({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['notification-preferences', propertyId],
    queryFn: () => getNotificationPreferences(propertyId),
    enabled: !!propertyId,
  });

  const mutation = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) =>
      updateNotificationPreferences(propertyId, patch),
    onSuccess: (updated) => {
      qc.setQueryData<NotificationPreferences>(['notification-preferences', propertyId], updated);
      toast.success('Preferences saved');
    },
    onError: () => toast.error('Failed to save preferences'),
  });

  const prefs = query.data;

  if (query.isPending) {
    return <p className="text-sm text-muted-foreground py-4">Loading preferences…</p>;
  }

  if (query.isError || !prefs) {
    return <p className="text-sm text-destructive py-4">Couldn't load preferences.</p>;
  }

  const update = (patch: Partial<NotificationPreferences>) => mutation.mutate(patch);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Notification preferences</CardTitle>
        <CardDescription>Choose what you're notified about.</CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        <ToggleRow
          id="notifyVisitorCheckin"
          title="Visitor check-in"
          description="Notify me when a visitor I invited arrives at the gate."
          checked={prefs.notifyVisitorCheckin}
          disabled={mutation.isPending}
          onChange={(v) => update({ notifyVisitorCheckin: v })}
        />
        <ToggleRow
          id="notifyVisitorCheckout"
          title="Visitor check-out"
          description="Notify me when a visitor leaves."
          checked={prefs.notifyVisitorCheckout}
          disabled={mutation.isPending}
          onChange={(v) => update({ notifyVisitorCheckout: v })}
        />
        <ToggleRow
          id="notifyAnnouncements"
          title="Announcements"
          description="Receive property announcements from your property manager."
          checked={prefs.notifyAnnouncements}
          disabled={mutation.isPending}
          onChange={(v) => update({ notifyAnnouncements: v })}
        />
      </CardContent>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function EmployeeAccountPage() {
  const ctx = useEmployeeContext();
  const propertyId = ctx?.propertyId ?? '';

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="text-2xl">My Account</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your profile and notifications</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <ProfileTab />
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <NotificationsTab propertyId={propertyId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
